import { z } from "zod";
import { AssessmentEnvelopeSchema } from "./assessment";

export const MAX_RECEIPT_BYTES = 128 * 1024;
const text = z.string().max(4096);
const id = z.string().min(1).max(256);
const texts = z.array(text).max(64);
const score = z.number().finite().min(0).max(100);
const gateStatus = z.enum(["ready", "open", "blocked"]);
const action = z.object({
  id, title: text, urgency: z.enum(["now", "next", "later"]),
  source: z.enum(["readiness", "studio_gate", "persona", "package"]),
  status: z.enum(["open", "planned", "done"]), owner: z.enum(["artist", "operator", "studio"]),
  closesGate: text.optional(), proof: text, rationale: text,
  difficulty: z.number().int().min(1).max(5)
}).strict();

/** V1 is supported as an unsigned, artist-owned continuity artifact only. */
export const SignalCapsuleReceiptV1Schema = z.object({
  receiptId: id, receiptVersion: z.literal("shadow-signal-receipt-v1"),
  capsule: z.object({
    capsuleId: id, artistName: z.string().min(1).max(300), generatedAt: z.string().datetime(),
    verdict: text, suggestedDepth: z.enum(["Signal", "Trace", "Echo", "Mirror", "Twin", "SHADOW"]),
    confidence: z.enum(["low", "medium", "high"]),
    // Earlier V1 exports did not have provenance. Absence stays unknown.
    assessment: AssessmentEnvelopeSchema.optional(),
    compositeScores: z.object({ music: score.optional(), identity: score.optional(), catalog: score.optional(), market: score.optional(), readiness: score.optional(), studioFit: score.optional(), overall: score }).strict(),
    readiness: z.object({ status: z.enum(["blocked", "draft", "ready_for_review"]), score, nextBestMove: text, openGates: texts }).strict(),
    nextActions: texts,
    actionPlan: z.object({ planId: id, headline: text, nextMove: text, items: z.array(action).max(64), boundaries: texts }).strict(),
    studioGate: z.object({
      decision: z.enum(["hold", "package", "review"]), label: text, riskLevel: z.enum(["low", "medium", "high"]),
      rationale: text, requiredProof: texts, escalationPath: text,
      lanes: z.array(z.object({ id, label: text, status: gateStatus, summary: text }).strict()).max(64), boundaries: texts
    }).strict(),
    seamline: z.object({ headline: text, bioShort: text }).strict(), shadowVoiceSummary: text, boundaries: texts
  }).strict(),
  integrity: z.object({ algorithm: z.literal("shadow-stable-fnv1a-64"), digest: z.string().regex(/^[0-9a-f]{16}$/), canonicalLength: z.number().int().min(1).max(MAX_RECEIPT_BYTES) }).strict(),
  signature: z.object({ status: z.literal("pending_obelisk"), reason: text }).strict(),
  importPolicy: z.object({ allowed: z.literal(true), mode: z.literal("artist_owned_continuity"), boundaries: texts }).strict()
}).strict();

/** Bound traversal before schema parsing, serialization or checksum recursion. */
export function receiptResourceCheck(value: unknown, options: { maxStringCharacters?: number; maxArrayItems?: number } = {}): string | null {
  const maxStringCharacters = Math.min(12000, Math.max(0, options.maxStringCharacters ?? 4096));
  const maxArrayItems = Math.min(150, Math.max(0, options.maxArrayItems ?? 64));
  const stack: Array<{ value: unknown; depth: number }> = [{ value, depth: 0 }];
  const seen = new Set<object>();
  let nodes = 0;
  while (stack.length) {
    const entry = stack.pop()!;
    if (++nodes > 4096 || entry.depth > 16) return "receipt_resource_limit";
    const item = entry.value;
    if (typeof item === "string") { if (item.length > maxStringCharacters) return "receipt_resource_limit"; continue; }
    if (item === null || item === undefined || typeof item === "boolean") continue;
    if (typeof item === "number") { if (!Number.isFinite(item)) return "malformed_receipt"; continue; }
    if (typeof item !== "object") return "malformed_receipt";
    if (seen.has(item)) return "malformed_receipt";
    seen.add(item);
    if (Array.isArray(item)) {
      if (item.length > maxArrayItems) return "receipt_resource_limit";
      for (const child of item) stack.push({ value: child, depth: entry.depth + 1 });
    } else {
      const prototype = Object.getPrototypeOf(item);
      if (prototype !== Object.prototype && prototype !== null) return "malformed_receipt";
      const descriptors = Object.getOwnPropertyDescriptors(item);
      const keys = Object.keys(descriptors);
      if (keys.length > 64) return "receipt_resource_limit";
      for (const key of keys) {
        if (["__proto__", "constructor", "prototype"].includes(key) || !("value" in descriptors[key])) return "malformed_receipt";
        stack.push({ value: descriptors[key].value, depth: entry.depth + 1 });
      }
    }
  }
  try {
    if (new TextEncoder().encode(JSON.stringify(value)).byteLength > MAX_RECEIPT_BYTES) return "receipt_resource_limit";
  } catch { return "malformed_receipt"; }
  return null;
}
